/**
 * Tabela de DeviceAccess (CRUD + teste) por device.
 *
 * Reusado em:
 *   - DeviceForm (modal de edição de host na lista de Hosts)
 *   - DeviceCredentialsTab (`/#device/<id>/credentials`)
 *
 * Endpoints: api.deviceAccessUpsert / deviceAccessDelete / deviceAccessTest.
 * Backend exige ADMIN em upsert/delete/test — UI mostra os botões pra todo mundo,
 * o 403 só dispara se for VIEWER/OPERATOR mesmo. (Casos de uso "limpo" são ADMIN.)
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { Modal, FormRow, Th, Td, ActionBtn, inp } from './_shared';

export type AccessRow = {
  id: number; kind: string; port: number;
  username: string | null; preferred: boolean;
  baseUrl: string | null; description?: string | null;
};

const KINDS = ['SSH', 'TELNET', 'HTTP', 'HTTPS', 'SNMP_V1', 'SNMP_V2C', 'SNMP_V3', 'NETCONF'];

export function AccessesSection({
  deviceId, accesses, headerless
}: {
  deviceId: number;
  accesses: AccessRow[];
  /** Quando true, omite o título "Acessos / Credenciais" + a borda superior — útil pra tab dedicada. */
  headerless?: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AccessRow | null>(null);
  const [creating, setCreating] = useState<{ kind: string } | null>(null);
  const del = useMutation({
    mutationFn: (id: number) => api.deviceAccessDelete(deviceId, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['device', deviceId] }); qc.invalidateQueries({ queryKey: ['devices'] }); }
  });

  const presentKinds = new Set(accesses.map(a => a.kind));
  const missingKinds = KINDS.filter(k => !presentKinds.has(k));

  return (
    <div className={headerless ? '' : 'mt-4 pt-3 border-t border-slate-800'}>
      <div className="flex items-center mb-2">
        {!headerless && <h3 className="text-sm font-semibold">Acessos / Credenciais</h3>}
        {missingKinds.length > 0 && (
          <select onChange={(e) => { if (e.target.value) { setCreating({ kind: e.target.value }); e.target.value = ''; } }}
            className={`${headerless ? '' : 'ml-auto'} bg-slate-950 ring-1 ring-slate-800 rounded px-2 py-1 text-xs`}>
            <option value="">+ adicionar...</option>
            {missingKinds.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        )}
      </div>
      {accesses.length === 0 ? (
        <div className="text-xs text-slate-500">Nenhum acesso cadastrado.</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-slate-400"><tr className="border-b border-slate-800">
            <Th>Kind</Th><Th>Port</Th><Th>User/baseUrl</Th><Th>Senha/Community</Th><Th>Pref</Th><Th className="w-20">Ações</Th>
          </tr></thead>
          <tbody>
            {accesses.map(a => (
              <tr key={a.id} className="border-b border-slate-800">
                <Td><span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-800 ring-1 ring-slate-700">{a.kind}</span></Td>
                <Td>{a.port}</Td>
                <Td>{a.username ?? a.baseUrl ?? '—'}</Td>
                <Td className="text-slate-500 italic">●●●●●●●● <span className="text-[10px]">(oculta)</span></Td>
                <Td>{a.preferred ? '★' : ''}</Td>
                <Td>
                  <ActionBtn onClick={() => setEditing(a)}>editar</ActionBtn>
                  <ActionBtn variant="danger" onClick={() => { if (confirm(`Remover acesso ${a.kind}?`)) del.mutate(a.id); }}>x</ActionBtn>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(editing || creating) && (
        <AccessForm
          deviceId={deviceId}
          access={editing ?? undefined}
          forceKind={creating?.kind}
          onClose={() => { setEditing(null); setCreating(null); }}
        />
      )}
    </div>
  );
}

export function AccessForm({ deviceId, access, forceKind, onClose }: { deviceId: number; access?: AccessRow; forceKind?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const kind = (access?.kind ?? forceKind) as string;
  const isSnmpCommunity = kind === 'SNMP_V1' || kind === 'SNMP_V2C';
  const isSnmpV3 = kind === 'SNMP_V3';
  const isWeb = kind === 'HTTP' || kind === 'HTTPS';
  const isShell = kind === 'SSH' || kind === 'TELNET' || kind === 'NETCONF';
  const defaultPort = ({ SSH: 22, TELNET: 23, HTTP: 80, HTTPS: 443, SNMP_V1: 161, SNMP_V2C: 161, SNMP_V3: 161, NETCONF: 830 } as any)[kind] ?? 0;

  const [form, setForm] = useState({
    port: access?.port ?? defaultPort,
    username: access?.username ?? '',
    password: '',
    secret: '',
    privPassword: '',
    authProto: 'SHA',
    privProto: 'AES',
    baseUrl: access?.baseUrl ?? '',
    preferred: access?.preferred ?? false,
    description: access?.description ?? ''
  });
  const [err, setErr] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const buildBody = () => {
    const body: any = { kind, port: Number(form.port) };
    if (form.username !== '') body.username = form.username;
    if (form.password !== '') body.password = form.password;
    if (form.secret !== '') body.secret = form.secret;
    if (form.privPassword !== '') body.privPassword = form.privPassword;
    if (isSnmpV3) { body.authProto = form.authProto; body.privProto = form.privProto; }
    if (isWeb) body.baseUrl = form.baseUrl || null;
    body.preferred = form.preferred;
    body.description = form.description || null;
    return body;
  };

  const save = useMutation({
    mutationFn: async () => {
      await api.deviceAccessUpsert(deviceId, buildBody());
      return api.deviceAccessTest(deviceId, buildBody());
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['device', deviceId] });
      qc.invalidateQueries({ queryKey: ['devices'] });
      setTestResult({ ok: !!r.ok, msg: r.ok ? `salvo + testado OK (${r.latencyMs} ms)` : `salvo, mas teste falhou: ${r.error ?? 'erro'} (${r.latencyMs} ms)` });
      setTimeout(() => onClose(), 1_500);
    },
    onError: (e: any) => setErr(e.detail?.error ?? e.message ?? 'erro')
  });

  const test = useMutation({
    mutationFn: () => api.deviceAccessTest(deviceId, buildBody()),
    onSuccess: (r) => {
      setTestResult({ ok: !!r.ok, msg: r.ok ? `OK (${r.latencyMs} ms)${r.info ? ' · ' + r.info : ''}` : `${r.error ?? 'falhou'} (${r.latencyMs} ms)` });
      qc.invalidateQueries({ queryKey: ['device', deviceId] });
      qc.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: (e: any) => setTestResult({ ok: false, msg: e.detail?.error ?? e.message ?? 'erro' })
  });

  return (
    <Modal title={`${access ? 'Editar' : 'Adicionar'} acesso ${kind}`} onClose={onClose}>
      <FormRow label="Port"><input type="number" className={inp} value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} /></FormRow>

      {(isShell || isWeb) && (
        <>
          <FormRow label="Username"><input className={inp} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></FormRow>
          <FormRow label={`Senha ${access ? '(em branco = manter atual)' : ''}`}>
            <input type="password" autoComplete="new-password" className={inp} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </FormRow>
        </>
      )}

      {isWeb && (
        <FormRow label="Base URL (opcional)"><input className={inp} value={form.baseUrl} onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} placeholder="https://10.x.x.x/cgi-bin" /></FormRow>
      )}

      {isSnmpCommunity && (
        <FormRow label={`Community ${access ? '(em branco = manter atual)' : ''}`}>
          <input type="password" autoComplete="new-password" className={inp} value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} placeholder="community" />
        </FormRow>
      )}

      {isSnmpV3 && (
        <>
          <FormRow label="Security Name (username)"><input className={inp} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></FormRow>
          <FormRow label="Auth Proto">
            <select className={inp} value={form.authProto} onChange={(e) => setForm({ ...form, authProto: e.target.value })}>
              <option>SHA</option><option>MD5</option>
            </select>
          </FormRow>
          <FormRow label={`Auth Password ${access ? '(em branco = manter)' : ''}`}>
            <input type="password" autoComplete="new-password" className={inp} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </FormRow>
          <FormRow label="Priv Proto">
            <select className={inp} value={form.privProto} onChange={(e) => setForm({ ...form, privProto: e.target.value })}>
              <option>AES</option><option>DES</option>
            </select>
          </FormRow>
          <FormRow label={`Priv Password ${access ? '(em branco = manter)' : ''}`}>
            <input type="password" autoComplete="new-password" className={inp} value={form.privPassword} onChange={(e) => setForm({ ...form, privPassword: e.target.value })} />
          </FormRow>
        </>
      )}

      <FormRow label="Preferred (usar como default)"><input type="checkbox" checked={form.preferred} onChange={(e) => setForm({ ...form, preferred: e.target.checked })} /></FormRow>
      <FormRow label="Descrição (opcional)"><input className={inp} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormRow>

      {err && <div className="text-rose-400 text-sm">{err}</div>}

      <div className="flex items-center justify-between gap-2 pt-3 mt-2 border-t border-slate-800">
        <button
          onClick={() => { setTestResult(null); test.mutate(); }}
          disabled={test.isPending}
          className={`px-3 py-1.5 rounded text-sm font-semibold ring-1 transition-colors disabled:opacity-50 ${
            test.isPending ? 'bg-slate-700 ring-slate-600 text-slate-200' :
            testResult == null ? 'bg-slate-800 hover:bg-slate-700 ring-slate-700 text-slate-200' :
            testResult.ok ? 'bg-emerald-600 hover:bg-emerald-500 ring-emerald-500 text-white' :
            'bg-rose-600 hover:bg-rose-500 ring-rose-500 text-white'
          }`}
          title={access ? 'Testa com a senha digitada; em branco usa a senha salva.' : 'Testa conexão com os dados informados.'}
        >
          {test.isPending ? 'testando…' : testResult == null ? 'testar conexão' : testResult.ok ? '✓ ok' : '✗ falhou'}
        </button>
        <div className={`flex-1 px-2 text-xs truncate ${testResult == null ? 'text-slate-500' : testResult.ok ? 'text-emerald-300' : 'text-rose-300'}`}>
          {testResult?.msg ?? ''}
        </div>
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-slate-400 hover:text-slate-200">cancelar</button>
        <button onClick={() => { setErr(null); save.mutate(); }} disabled={save.isPending}
                className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-sm font-semibold">
          {save.isPending ? 'salvando…' : 'salvar'}
        </button>
      </div>
    </Modal>
  );
}
